import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  createGraphqlClientError,
  toGraphqlClientError,
} from 'src/app/entities/order/lib/graphql-error.utils';

@Injectable({
  providedIn: 'root',
})
export class GraphService {
  constructor(private readonly httpClient: HttpClient) {}

  public queryGraphQLSourcing<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return this.httpClient
      .post<{ data: T }>(`${environment.sourcingApi}/graphql`, {
        //.post<{ data: T }>('http://localhost:3000/graphql', {
        query: options.query,
        variables: options.variables,
      })
      .pipe(map((d) => d.data));
  }

  /**
   * Executes a GraphQL query against Sourcing API returning the full envelope including errors and metadata.
   *
   * @param options - Query string and optional variables.
   * @returns Observable containing data payload and any GraphQL error extensions.
   */
  public queryGraphQLSourcingEnvelope<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<{
    data: T | null;
    errors?: Array<{
      message?: string;
      extensions?: {
        response?: { code?: string; message?: string };
        code?: string;
      };
    }>;
  }> {
    return this.httpClient.post<{
      data: T | null;
      errors?: Array<{
        message?: string;
        extensions?: {
          response?: { code?: string; message?: string };
          code?: string;
        };
      }>;
    }>(`${environment.sourcingApi}/graphql`, {
      query: options.query,
      variables: options.variables,
    });
  }

  public mutateGraphQLSourcing<T>(options: {
    mutation: string;
    variables?: { [key: string]: any };
  }): Observable<any> {
    return this.httpClient
      .post<{ data: T }>(`${environment.sourcingApi}/graphql`, {
        //.post<{ data: T }>('http://localhost:3000/graphql', {
        query: options.mutation,
        variables: options.variables,
      })
      .pipe(map((d) => d.data));
  }

  public queryGraphQLSales<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return this.httpClient
      .post<{ data: T }>(`${environment.salesApi}/graphql`, {
        query: options.query,
        variables: options.variables,
      })
      .pipe(map((d) => d.data));
  }

  public mutateGraphQLSales<T>(options: {
    mutation: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return this.httpClient
      .post<{ data: T }>(`${environment.salesApi}/graphql`, {
        query: options.mutation,
        variables: options.variables,
      })
      .pipe(map((d) => d.data));
  }

  public queryGraphQLProduct<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return this.httpClient
      .post<{ data: T }>(`${environment.productApi}/graphql`, {
        query: options.query,
        variables: options.variables,
      })
      .pipe(map((d) => d.data));
  }

  public mutateGraphQLProduct<T>(options: {
    mutation: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return this.httpClient
      .post<{ data: T }>(`${environment.productApi}/graphql`, {
        query: options.mutation,
        variables: options.variables,
      })
      .pipe(map((d) => d.data));
  }

  public queryGraphQLAlimentosBebidas<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return this.httpClient
      .post<{ data: T }>(`${environment.alimentosBebidasApi}/graphql`, {
        query: options.query,
        variables: options.variables,
      })
      .pipe(map((d) => d.data));
  }

  public mutationAybApi<T>(options: {
    mutation: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return this.httpClient
      .post<{ data: T }>(`${environment.alimentosBebidasApi}/graphql`, {
        query: options.mutation,
        variables: options.variables,
      })
      .pipe(
        map((response: any) => {
          if (response.errors) {
            throw new Error(
              response.errors.map((err: any) => err.message).join(', '),
            );
          }
          return response.data;
        }),
        catchError((error) => {
          console.warn(error);
          return throwError(
            () =>
              new Error(
                error.message ||
                  'Hubo un error, por favor inténtelo mas tarde.',
              ),
          );
        }),
      );
  }

  public queryGraphQLOperations<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return this.httpClient
      .post<{ data: T }>(`${environment.operationsApi}/graphql`, {
        query: options.query,
        variables: options.variables,
      })
      .pipe(map((d) => d.data));
  }

  public mutateGraphQLOperations<T>(options: {
    mutation: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return this.httpClient
      .post<{ data: T }>(`${environment.operationsApi}/graphql`, {
        query: options.mutation,
        variables: options.variables,
      })
      .pipe(map((d) => d.data));
  }

  /**
   * Executes a GraphQL query against the Core API (api-explorandes-core).
   *
   * Description:
   *   Dispatches an HTTP POST request to `${environment.coreApi}/graphql` with the provided query and variables,
   *   returning the resulting payload or GraphQL envelope with errors if present.
   *
   * Parameters:
   *   requestOptions: { query: string; variables?: { [parameterKey: string]: any } } - GraphQL query string and variables.
   *
   * Return Value:
   *   Observable<T> - Response data payload or envelope with error details.
   *
   * Exceptions & Errors:
   *   Emits an Error if the HTTP request encounters a network or server communication failure.
   */
  public queryGraphQLCore<T>(requestOptions: {
    query: string;
    variables?: { [parameterKey: string]: any };
  }): Observable<T> {
    return this.httpClient
      .post<{ data: T }>(
        `${environment.coreApi}/graphql`,
        {
          query: requestOptions.query,
          variables: requestOptions.variables,
        }
      )
      .pipe(
        map((responsePayload: any) => {
          if (responsePayload?.errors) {
            return {
              data: responsePayload.data,
              errors: responsePayload.errors,
            } as unknown as T;
          }
          return responsePayload?.data as T;
        }),
        catchError((httpErrorPayload: any) => {
          console.error('❌ Error en petición a Core API:', httpErrorPayload);
          return throwError(
            () =>
              new Error(
                httpErrorPayload?.message ||
                  'Hubo un error ,por favor intentelo mas tarde.'
              )
          );
        })
      );
  }

  /**
   * Executes a GraphQL mutation against the Core API (api-explorandes-core).
   *
   * Description:
   *   Dispatches an HTTP POST request to `${environment.coreApi}/graphql` with the provided mutation and variables,
   *   returning data payload on success or throwing a normalized GraphQLClientError on failure.
   *
   * Parameters:
   *   requestOptions: { mutation: string; variables?: { [parameterKey: string]: any } } - GraphQL mutation string and variables.
   *
   * Return Value:
   *   Observable<any> - Mutation response data payload.
   *
   * Exceptions & Errors:
   *   Throws GraphQLClientError if GraphQL operation returns errors or HTTP request fails.
   */
  public mutateGraphQLCore<T>(requestOptions: {
    mutation: string;
    variables?: { [parameterKey: string]: any };
  }): Observable<any> {
    return this.httpClient
      .post<any>(
        `${environment.coreApi}/graphql`,
        {
          query: requestOptions.mutation,
          variables: requestOptions.variables,
        }
      )
      .pipe(
        map((responsePayload: any) => {
          if (responsePayload?.errors && responsePayload.errors.length > 0) {
            throw createGraphqlClientError(responsePayload.errors);
          }
          return responsePayload?.data;
        }),
        catchError((httpErrorPayload: any) => {
          console.error('❌ Error en mutación a Core API:', httpErrorPayload);
          return throwError(() => toGraphqlClientError(httpErrorPayload));
        })
      );
  }
}
